/**
 * PostponeVisitModal — Bestätigungs-Dialog zum Verschieben eines Vor-Ort-Termins.
 *
 * Zeigt das Original-Datum, fragt neues Datum ab (DatePickerInput)
 * und ruft POST /visits/:id/postpone auf.
 *
 * Backend legt einen neuen `planned`-Termin an und setzt den Original-Termin
 * auf `postponed` mit `successor_visit_id`.
 */
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { usePostponeVisit } from "../../hooks/useVisits.js";
import { toLocalDateString } from "../../lib/date.js";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

interface PostponeVisitModalProps {
  opened: boolean;
  onClose: () => void;
  visitId: string;
  /** ISO date string des ursprünglichen Termins */
  originalScheduledAt: string;
  customerName?: string;
}

interface PostponeFormValues {
  newScheduledAt: Date | null;
}

export function PostponeVisitModal({
  opened,
  onClose,
  visitId,
  originalScheduledAt,
  customerName,
}: PostponeVisitModalProps) {
  const { t } = useTranslation();
  const postpone = usePostponeVisit(visitId);

  const form = useForm<PostponeFormValues>({
    initialValues: {
      newScheduledAt: null,
    },
    validate: {
      newScheduledAt: (val) => {
        if (!val) return t("pages.visits.postponePickNewDate");
        const original = new Date(originalScheduledAt);
        if (val <= original) return t("pages.visits.postponeError");
        return null;
      },
    },
  });

  function handleClose() {
    form.reset();
    onClose();
  }

  async function handleSubmit(values: PostponeFormValues) {
    if (!values.newScheduledAt) return;

    // toLocalDateString statt toISOString (Befund B1) — sonst rutscht das
    // lokal zur Mitternacht gewählte Datum in MESZ/MEZ auf den Vortag.
    const newScheduledAt = toLocalDateString(values.newScheduledAt);
    const payload = {
      newScheduledAt,
    };

    try {
      await postpone.mutateAsync(payload);
      handleClose();
    } catch {
      // Fehler wird über postpone.error angezeigt
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("pages.visits.postponeTitle")}
      size="sm"
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
        <Stack gap="md">
          {/* Kontext */}
          <Stack gap={4}>
            {customerName && (
              <Text size="sm">
                <Text span fw={600}>{t("pages.visits.customerLabel")} </Text>
                {customerName}
              </Text>
            )}
            <Text size="sm">
              <Text span fw={600}>{t("pages.visits.postponeOriginal")} </Text>
              {formatDate(originalScheduledAt)}
            </Text>
          </Stack>

          <Text size="sm" c="dimmed">
            {t("pages.visits.postponeDescription")}
          </Text>

          {/* Neues Datum */}
          <DatePickerInput
            label={t("pages.visits.postponeNewDate")}
            withAsterisk
            placeholder={t("pages.visits.postponeNewDatePlaceholder")}
            value={form.values.newScheduledAt}
            onChange={(val) => form.setFieldValue("newScheduledAt", val)}
            error={form.errors.newScheduledAt as string | undefined}
            minDate={new Date()}
            locale="de"
          />

          {/* Fehler */}
          {postpone.isError && (
            <Alert color="red" title={t("common.error")}>
              {postpone.error instanceof Error
                ? postpone.error.message
                : t("pages.visits.postponeErrorGeneric")}
            </Alert>
          )}

          {/* Aktionen */}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={postpone.isPending}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              color="orange"
              loading={postpone.isPending}
            >
              {t("pages.visits.postponeSubmit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
